import React, { useState, useRef, useEffect } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { cn } from "@/lib/utils";

interface EnhancedTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  id: string;
  register?: any;
  setValue?: any;
  variables: string[];
}

export const EnhancedTextarea: React.FC<EnhancedTextareaProps> = ({
  id,
  register,
  setValue,
  variables,
  className,
  ...props
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [isTypingVariable, setIsTypingVariable] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [filteredVariables, setFilteredVariables] = useState(variables);
  const [searchText, setSearchText] = useState("");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showPopup) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = (prev + 1) % filteredVariables.length;
          scrollSelectedIntoView(newIndex);
          return newIndex;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex =
            (prev - 1 + filteredVariables.length) % filteredVariables.length;
          scrollSelectedIntoView(newIndex);
          return newIndex;
        });
      } else if (e.key === "Enter" && showPopup) {
        e.preventDefault();
        insertVariable(filteredVariables[selectedIndex]);
      } else if (e.key === "Escape") {
        setShowPopup(false);
        setIsTypingVariable(false);
        setSelectedIndex(0);
        setSearchText("");
      } else if (e.key.length === 1) {
        const textarea = e.currentTarget;
        const value = textarea.value;
        const start = textarea.selectionStart;
        const lastSlashIndex = value.substring(0, start).lastIndexOf("/");

        if (lastSlashIndex !== -1) {
          const typedText = value.substring(lastSlashIndex + 1, start) + e.key;
          setSearchText(typedText);

          const filtered = variables.filter((v) =>
            v.toLowerCase().includes(typedText.toLowerCase())
          );

          if (filtered.length > 0) {
            setFilteredVariables(filtered);
            setSelectedIndex(0);
          } else {
            setFilteredVariables([]);
          }
        }
      }
    }

    const textarea = e.currentTarget;
    const start = textarea.selectionStart;
    const value = textarea.value;

    if (e.key === "/") {
      e.preventDefault();
      if (!textarea) return;

      // Check if the slash is the first character, has a space before it, or is at the start of a new line
      const charBefore = value[start - 1];
      const shouldShowPopup =
        start === 0 || charBefore === " " || charBefore === "\n";

      if (!shouldShowPopup) {
        // Just insert the slash without showing popup
        const newValue =
          value.substring(0, start) + "/" + value.substring(start);
        setValue(register.name, newValue);

        setTimeout(() => {
          if (textarea) {
            const newPosition = start + 1;
            textarea.setSelectionRange(newPosition, newPosition);
          }
        }, 0);
        return;
      }

      setFilteredVariables(variables);

      textareaRef.current = textarea;
      setIsTypingVariable(true);
      setSelectedIndex(0);

      const newValue = value.substring(0, start) + "/" + value.substring(start);
      setValue(register.name, newValue);

      setTimeout(() => {
        if (textarea) {
          const newPosition = start + 1;
          textarea.setSelectionRange(newPosition, newPosition);

          const rect = textarea.getBoundingClientRect();
          const cursorPosition = getCaretCoordinates(textarea, newPosition);

          setPopupPosition({
            top: rect.top + cursorPosition.top + window.scrollY + 24,
            left: rect.left + cursorPosition.left + window.scrollX,
          });
          setShowPopup(true);
        }
      }, 0);
    } else if (e.key === "Backspace") {
      const charBeforeCursor = value[start - 1];

      if (charBeforeCursor === "/") {
        setShowPopup(false);
        setIsTypingVariable(false);
        setFilteredVariables(variables);
        setSearchText("");
        setSelectedIndex(0);
      } else if (isTypingVariable) {
        const lastSlashIndex = value.substring(0, start).lastIndexOf("/");
        if (lastSlashIndex !== -1) {
          const typedText = value.substring(lastSlashIndex + 1, start - 1);
          setSearchText(typedText);

          if (typedText === "") {
            setFilteredVariables(variables);
          } else {
            const filtered = variables.filter((v) =>
              v.toLowerCase().includes(typedText.toLowerCase())
            );
            setFilteredVariables(filtered);
          }
          setSelectedIndex(0);
        }
      }
    } else if (e.key === " " && isTypingVariable) {
      setShowPopup(false);
      setIsTypingVariable(false);
    } else if (e.key === "Escape") {
      setShowPopup(false);
      setIsTypingVariable(false);
      setSelectedIndex(0);
    }
  };

  const scrollSelectedIntoView = (index: number) => {
    if (!popupRef.current) return;

    const popupElement = popupRef.current;
    const selectedElement = popupElement.children[0].children[
      index
    ] as HTMLElement;

    if (!selectedElement) return;

    const popupRect = popupElement.getBoundingClientRect();
    const selectedRect = selectedElement.getBoundingClientRect();

    if (selectedRect.bottom > popupRect.bottom) {
      selectedElement.scrollIntoView({ block: "nearest" });
    } else if (selectedRect.top < popupRect.top) {
      selectedElement.scrollIntoView({ block: "nearest" });
    }
  };

  const insertVariable = (variable: string) => {
    const textarea =
      textareaRef.current ||
      (document.getElementById(register.name) as HTMLTextAreaElement);
    if (!textarea) return;

    const start = textarea.selectionStart;
    const value = textarea.value;
    const lastSlashIndex = showPopup
      ? value.substring(0, start).lastIndexOf("/")
      : start;

    const variableWithBraces = `{{${variable}}}`;
    const beforeText = showPopup
      ? value.substring(0, lastSlashIndex).replace(/\{+$/, "")
      : value.substring(0, start);

    const newValue =
      beforeText + variableWithBraces + " " + value.substring(start);

    setValue(register.name, newValue);

    setTimeout(() => {
      if (textarea) {
        const newPosition =
          (showPopup ? lastSlashIndex : start) + variableWithBraces.length + 1;
        textarea.focus();
        textarea.setSelectionRange(newPosition, newPosition);
      }
    }, 0);

    setShowPopup(false);
    setIsTypingVariable(false);
    setFilteredVariables(variables);
    setSearchText("");
    setSelectedIndex(0);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const popupElement = document.querySelector(".fixed.bg-white");
      const isClickingVariable = (target as HTMLElement).classList?.contains(
        "px-4"
      );

      if (
        !isClickingVariable &&
        popupElement &&
        !popupElement.contains(target)
      ) {
        setShowPopup(false);
        setIsTypingVariable(false);
        setFilteredVariables(variables);
        setSearchText("");
        setSelectedIndex(0);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div style={{ position: "relative" }}>
      <TextareaAutosize
        id={`${register.name}`}
        className={cn(
          "min-h-[250px] hover:bg-gray-25/30 focus:bg-gray-25/30 focus:border-2 focus:border-gray-100 dark:hover:bg-gray-800 dark:focus:bg-gray-800 dark:focus:border-2 dark:focus:border-gray-800 flex w-full rounded-md bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        ref={textareaRef}
        onKeyDown={handleKeyDown}
        {...register}
        {...props}
        style={{
          resize: "none",
          borderWidth: "2px",
          borderColor: "transparent",
        }}
      />
      {showPopup && (
        <div
          ref={popupRef}
          className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg"
          style={{
            top: popupPosition.top,
            left: popupPosition.left,
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 9999,
            scrollbarWidth: "thin",
            scrollbarColor: "rgb(156 163 175) transparent",
          }}
        >
          <div className="overflow-auto">
            {filteredVariables.length > 0 ? (
              filteredVariables.map((variable, index) => (
                <div
                  key={variable}
                  className={cn(
                    "px-4 py-2 cursor-pointer",
                    selectedIndex === index
                      ? "bg-gray-100 dark:bg-gray-700"
                      : "hover:bg-gray-100 dark:hover:bg-gray-700",
                    "whitespace-nowrap"
                  )}
                  onClick={() => insertVariable(variable)}
                  style={{
                    position: "relative",
                  }}
                >
                  {variable}
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-gray-500 dark:text-gray-400">
                No matching variables
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function getCaretCoordinates(element: HTMLTextAreaElement, position: number) {
  if (!element) return { top: 0, left: 0 };

  try {
    const div = document.createElement("div");
    const computed = window.getComputedStyle(element);
    if (!computed) return { top: 0, left: 0 };

    const styles = {
      "padding-left": computed.getPropertyValue("padding-left"),
      "padding-top": computed.getPropertyValue("padding-top"),
      "border-left-width": computed.getPropertyValue("border-left-width"),
      "border-top-width": computed.getPropertyValue("border-top-width"),
      "box-sizing": computed.getPropertyValue("box-sizing"),
      "white-space": "pre-wrap",
      "word-wrap": "break-word",
      position: "absolute",
      visibility: "hidden",
      width: `${element.offsetWidth}px`,
    };

    Object.assign(div.style, styles);

    div.textContent = element.value.substring(0, position);

    const span = document.createElement("span");
    span.textContent = element.value.substring(position) || ".";
    div.appendChild(span);

    document.body.appendChild(div);

    const coordinates = {
      top: span.offsetTop,
      left: span.offsetLeft,
    };

    document.body.removeChild(div);
    return coordinates;
  } catch (error) {
    console.error("Error calculating caret coordinates:", error);
    return { top: 0, left: 0 };
  }
}
